import { STATUS_META } from "@/lib/format";
import { cn } from "@/lib/utils";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

export function ErrorState({ onRetry, message = "Something went wrong while loading this page." }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[#DC2626]/30 bg-[#DC2626]/[0.04] px-6 py-16 text-center" data-testid="error-state">
      <div className="mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-[#DC2626]/12 text-error">
        <AlertTriangle className="h-6 w-6" strokeWidth={2} />
      </div>
      <h3 className="font-display text-base font-bold text-ink">Couldn't load</h3>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">{message}</p>
      {onRetry && (
        <Button variant="outline" onClick={onRetry} data-testid="error-retry-btn" className="mt-5 gap-2">
          <RefreshCw className="h-4 w-4" /> Try again
        </Button>
      )}
    </div>
  );
}

export function StatusBadge({ status, className }) {
  const m = STATUS_META[status] || { label: status, cls: "bg-muted text-muted-foreground border-border" };
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold",
        m.cls,
        className
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: m.dot }} />
      {m.label}
    </span>
  );
}

export function PageHeader({ title, subtitle, children, testid }) {
  return (
    <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" data-testid={testid}>
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, hint, action }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-white/60 px-6 py-16 text-center" data-testid="empty-state">
      {Icon && (
        <div className="mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-accent text-accent-foreground">
          <Icon className="h-6 w-6" strokeWidth={2} />
        </div>
      )}
      <h3 className="font-display text-base font-bold text-ink">{title}</h3>
      {hint && <p className="mt-1 max-w-sm text-sm text-muted-foreground">{hint}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Skeletons({ count = 6 }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="h-32 animate-pulse rounded-xl border border-border bg-muted/50" />
      ))}
    </div>
  );
}
